import React from 'react';
import { Flex, Text, MultiSegmentGauge, CollapsibleSection, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import { CaretDown } from '@phosphor-icons/react';
import {
  REVIEW_CATEGORIES,
  scoreBand,
  type ReviewCategoryKey,
  type ReviewComment,
  type ReviewCommentSeverity,
  type ReviewScoreBand,
  type ReviewScorecard as ReviewScorecardData,
} from '@chatsdet/types';

/**
 * Scorecard for an AI flow review: one centered overall gauge, a row of small
 * per-category gauges, and comments grouped by severity into collapsible
 * sections. Renders inline by default, or as a collapsible panel (collapsed in
 * the proposal review so the diff stands out).
 *
 * Band colors reuse the app's colorblind-friendly palette — blue for good,
 * orange for needs-work — with red reserved for poor / error-severity findings.
 */

const BAND_COLOR: Record<ReviewScoreBand, string> = {
  good: '#4DA6FF', // blue
  'needs-work': '#FFBD0F', // orange
  poor: '#E5484D', // red
};

const BAND_TEXT: Record<ReviewScoreBand, string> = {
  good: 'In good shape',
  'needs-work': 'Some things to tighten up',
  poor: 'Needs attention before approving',
};

const SEVERITY_COLOR: Record<ReviewCommentSeverity, string> = {
  info: '#4DA6FF',
  warning: '#FFBD0F',
  error: '#E5484D',
};

/** Severity groups, in priority order, with their section labels. */
const SEVERITY_GROUPS: { severity: ReviewCommentSeverity; label: string }[] = [
  { severity: 'error', label: 'Errors' },
  { severity: 'warning', label: 'Warnings' },
  { severity: 'info', label: 'Suggestions' },
];

const CATEGORY_SHORT = new Map(REVIEW_CATEGORIES.map((c) => [c.key, c.short]));
const CATEGORY_DESC = new Map(REVIEW_CATEGORIES.map((c) => [c.key, c.description]));

export const ReviewScorecard: React.FC<{
  scorecard: ReviewScorecardData;
  comments: ReviewComment[];
  /** Render as a collapsible panel with a compact header. */
  collapsible?: boolean;
  /** Initial expanded state when collapsible (default: collapsed). */
  defaultExpanded?: boolean;
}> = ({ scorecard, comments, collapsible = false, defaultExpanded = false }) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const band = scoreBand(scorecard.overall);

  const body = (
    <Flex direction="column" gap="lg">
      <CategoryGauges scorecard={scorecard} />
      <CommentGroups comments={comments} />
    </Flex>
  );

  if (!collapsible) {
    return (
      <Flex direction="column" gap="lg">
        <OverallGauge scorecard={scorecard} band={band} />
        {body}
      </Flex>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
        aria-expanded={expanded}
      >
        <Flex align="center" gap="sm">
          <MultiSegmentGauge
            size="xs"
            total={100}
            centerValue={String(scorecard.overall)}
            segments={[{ label: 'score', value: scorecard.overall, color: BAND_COLOR[band] }]}
          />
          <div>
            <Text className="text-sm font-semibold">Review</Text>
            <Text className="text-xs text-muted-foreground">
              {scorecard.overall}/100 · {BAND_TEXT[band]}
            </Text>
          </div>
        </Flex>
        <Flex align="center" gap="sm">
          {comments.length > 0 && (
            <Text className="text-xs text-muted-foreground">
              {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
            </Text>
          )}
          <CaretDown
            size={14}
            weight="bold"
            className={`text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </Flex>
      </button>
      {expanded && <div className="mt-4">{body}</div>}
    </div>
  );
};

/** Big centered overall gauge with band caption beneath it. */
const OverallGauge: React.FC<{ scorecard: ReviewScorecardData; band: ReviewScoreBand }> = ({
  scorecard,
  band,
}) => (
  <Flex direction="column" align="center" gap="xs" className="py-1">
    <MultiSegmentGauge
      size="sm"
      total={100}
      centerValue={String(scorecard.overall)}
      centerLabel="/ 100"
      segments={[{ label: 'score', value: scorecard.overall, color: BAND_COLOR[band] }]}
    />
    <Text className="text-sm font-semibold">Overall review score</Text>
    <Text className="text-xs text-muted-foreground">{BAND_TEXT[band]}</Text>
  </Flex>
);

/** Row of per-category gauges with stronger labels + a tooltip describing what
 *  each one measures. Spaced evenly across the (wide) modal. */
const CategoryGauges: React.FC<{ scorecard: ReviewScorecardData }> = ({ scorecard }) => (
  <div className="flex flex-wrap justify-around gap-x-8 gap-y-4 border-t border-border/50 pt-5">
    {scorecard.categories.map((cat) => {
      const key = cat.key as ReviewCategoryKey;
      const catBand = scoreBand(cat.score);
      return (
        <TooltipTrigger key={cat.key} delay={150}>
          <button type="button" className="flex cursor-default flex-col items-center gap-1.5">
            <MultiSegmentGauge
              size="xs"
              total={100}
              centerValue={String(cat.score)}
              segments={[{ label: cat.key, value: cat.score, color: BAND_COLOR[catBand] }]}
            />
            <Text className="text-center text-xs font-semibold text-foreground">
              {CATEGORY_SHORT.get(key) ?? cat.key}
            </Text>
          </button>
          <Tooltip>
            <span className="block max-w-[240px] text-xs">{CATEGORY_DESC.get(key) ?? ''}</span>
          </Tooltip>
        </TooltipTrigger>
      );
    })}
  </div>
);

/** Comments grouped by severity into collapsible sections. The highest-priority
 *  non-empty group starts expanded; the rest are collapsed. */
const CommentGroups: React.FC<{ comments: ReviewComment[] }> = ({ comments }) => {
  if (comments.length === 0) return null;
  const topSeverity = SEVERITY_GROUPS.find(
    ({ severity }) => comments.some((c) => c.severity === severity),
  )?.severity;
  return (
    <div>
      {SEVERITY_GROUPS.map(({ severity, label }) => {
        const group = comments.filter((c) => c.severity === severity);
        if (group.length === 0) return null;
        return (
          <CollapsibleSection
            key={severity}
            label={label}
            count={group.length}
            defaultExpanded={severity === topSeverity}
            icon={
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: SEVERITY_COLOR[severity] }}
              />
            }
          >
            <Flex direction="column" gap="md" className="pl-6">
              {group.map((comment, i) => (
                <ReviewCommentRow key={i} comment={comment} />
              ))}
            </Flex>
          </CollapsibleSection>
        );
      })}
    </div>
  );
};

const ReviewCommentRow: React.FC<{ comment: ReviewComment }> = ({ comment }) => (
  <div>
    <Text className="text-sm font-medium">{comment.title}</Text>
    <Text className="mt-0.5 text-xs text-muted-foreground">{comment.message}</Text>
    {comment.suggestion && (
      <Text className="mt-1 text-xs">
        <span className="font-medium">Suggestion: </span>
        {comment.suggestion}
      </Text>
    )}
  </div>
);
